type NestedObject = { [key: string]: any }

type IsPlainObject<T> = T extends object
  ? T extends any[]
    ? false
    : true
  : false

export type NestedKeys<T> = {
  [K in keyof T & string]: IsPlainObject<NonNullable<T[K]>> extends true
    ? K | `${K}.${NestedKeys<NonNullable<T[K]>>}`
    : K
}[keyof T & string]

const isFalsy = (value: any): boolean =>
  value === null ||
  value === undefined ||
  value === "" ||
  value === false ||
  (Array.isArray(value) && value.length === 0)

export function isFormChanged<T = NestedObject>(
  obj1: T,
  obj2: Partial<T> | NestedObject,
  ignoreKeys?: Array<NestedKeys<T>>,
  debug = false
): boolean {
  const ignoreSet = ignoreKeys?.length ? new Set(ignoreKeys) : null

  const isDifferent = (o1: any, o2: any, path: string): boolean => {
    if (ignoreSet?.has(path as any)) return false
    if (o1 === o2) return false

    const o1Falsy = isFalsy(o1)
    const o2Falsy = isFalsy(o2)

    if (o1Falsy && o2Falsy) return false
    if (o1Falsy || o2Falsy) {
      if (debug) console.log(`📍 ${path}:`, o1, "→", o2)
      return true
    }

    const t1 = typeof o1
    const t2 = typeof o2

    // Числове порівняння
    if (t1 !== t2) {
      if (t1 === "number" || t2 === "number") {
        if (+o1 === +o2 && !isNaN(+o1)) return false
      }
      if (debug) console.log(`📍 ${path}:`, o1, "→", o2)
      return true
    }

    // Примітиви
    if (t1 !== "object") {
      // Числове порівняння для однакових типів
      if (t1 === "number" || !isNaN(+o1)) {
        if (+o1 === +o2) return false
      }
      if (debug) console.log(`📍 ${path}:`, o1, "→", o2)
      return true
    }

    // Масиви
    if (Array.isArray(o1)) {
      if (!Array.isArray(o2) || o1.length !== o2.length) {
        if (debug) console.log(`📍 ${path}:`, o1, "→", o2)
        return true
      }
      for (let i = 0; i < o1.length; i++) {
        if (isDifferent(o1[i], o2[i], `${path}[${i}]`)) return true
      }
      return false
    }

    // Об'єкти
    const keys = Object.keys(o1)
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i]
      const currentPath = path ? `${path}.${key}` : key
      if (ignoreSet?.has(currentPath as any)) continue
      if (isDifferent(o1[key], o2?.[key], currentPath)) return true
    }

    return false
  }

  return isDifferent(obj1, obj2, "")
}
